import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import devSeeqBlueIcon from 'resources/img/dev_seeq_dkblue2026.ico';
import devSeeqGreenIcon from 'resources/img/dev_seeq_green2026.ico';
import devIcon from 'resources/img/dev_seeq_logo_fav2026.ico';
import devSeeqActionBoardIcon from 'resources/img/dev_seeq_ltblue2026.ico';
import devSeeqPurpleIcon from 'resources/img/dev_seeq_purple_fav2026.ico';
import seeqBlueIcon from 'resources/img/seeq_dkblue_2026.ico';
import seeqGreenIcon from 'resources/img/seeq_green_2026.ico';
import seeqActionBoardIcon from 'resources/img/seeq_ltblue_2026.ico';
import seeqPurpleIcon from 'resources/img/seeq_purple_2026.ico';
import seeqIcon from 'resources/img/seeq_white_2026.ico';
import {
  APPSERVER_API_CONTENT_TYPE as API_APPSERVER_API_CONTENT_TYPE,
  APPSERVER_API_PREFIX as API_APPSERVER_API_PREFIX,
} from '@seeqdev/api';
import type { unitOfTime } from 'moment-timezone';

export interface DurationTimeUnit {
  unit: readonly string[];
  momentUnit?: unitOfTime.DurationConstructor;
  translationKey: string;
}

export interface UnitWithLabel {
  unit: readonly string[];
  shortLabel: string;
}

// To update the Insights Board favicon in the future, simply replace:
// - client/packages/webserver/app/resources/img/seeq_insights.ico
// - client/packages/webserver/app/resources/img/dev_seeq_insights.ico
export const FAVICON =
  process.env.NODE_ENV === 'development'
    ? {
        GREEN: devSeeqGreenIcon,
        BLUE: devSeeqBlueIcon,
        PURPLE: devSeeqPurpleIcon,
        WORKBENCH: devIcon,
        ACTION_BOARD: devSeeqActionBoardIcon,
      }
    : {
        GREEN: seeqGreenIcon,
        BLUE: seeqBlueIcon,
        PURPLE: seeqPurpleIcon,
        WORKBENCH: seeqIcon,
        ACTION_BOARD: seeqActionBoardIcon,
      };

export const JOURNAL_PREFIX_PATH = '/links';

export const API_TYPES = {
  ASSET: SeeqNames.Types.Asset,
  CALCULATED_CONDITION: SeeqNames.Types.CalculatedCondition,
  CALCULATED_SIGNAL: SeeqNames.Types.CalculatedSignal,
  DATASOURCE: SeeqNames.Types.Datasource,
  DATAFILE: SeeqNames.Types.Datafile,
  CAPSULE: 'SimpleCapsule',
  CALCULATED_SCALAR: SeeqNames.Types.CalculatedScalar,
  STORED_CONDITION: SeeqNames.Types.StoredCondition,
  STORED_SIGNAL: SeeqNames.Types.StoredSignal,
  LITERAL_SCALAR: SeeqNames.Types.LiteralScalar,
  USER: SeeqNames.Types.User,
  ANALYSIS: SeeqNames.Types.Analysis,
  TOPIC: SeeqNames.Types.Topic,
  ACTION_BOARD: SeeqNames.Types.ActionBoard,
  WORKSHEET: SeeqNames.Types.Worksheet,
  TABLE: SeeqNames.Types.Chart,
  JOURNAL: SeeqNames.Types.Journal,
  REPORT: SeeqNames.Types.Report,
  THRESHOLD_METRIC: SeeqNames.Types.ThresholdMetric,
  FORMULA_FUNCTION: SeeqNames.Types.AggregatingFormulaFunction,
  DISPLAY: SeeqNames.Types.Display,
  DISPLAY_TEMPLATE: SeeqNames.Types.DisplayTemplate,
  TABLE_DEFINITION: SeeqNames.Types.TableDefinition,
  HIERARCHY: SeeqNames.Types.Hierarchy,
  MODEL: SeeqNames.Types.Model,
  MODEL_CONTAINER: SeeqNames.Types.ModelContainer,
  STATISTICS_SUMMARY: SeeqNames.Types.FormulaItem,
  ONNX: SeeqNames.Types.FormulaItem,
};
export const ASSET_PATH_SEPARATOR = ' » ';

export const ITEM_METADATA = {
  valueUnitOfMeasure: 'Value Unit Of Measure',
  interpolationMethod: 'Interpolation Method',
  keyUnitOfMeasure: 'Key Unit Of Measure',
  maxInterpolation: 'Maximum Interpolation',
  maxCapsuleDuration: 'Maximum Capsule Duration',
};
export const STRING_UOM = 'string';
export const SEARCH_TYPES: string[] = [
  SeeqNames.Types.Asset,
  SeeqNames.Types.Condition,
  SeeqNames.Types.Signal,
  SeeqNames.Types.Scalar,
  SeeqNames.Types.Chart,
  SeeqNames.Types.ThresholdMetric,
  SeeqNames.Types.Datafile,
  SeeqNames.Types.Display,
];
export const SCALING_TABLE_TYPE = 'Scaling_Table';
export const ITEM_TABLE_TYPE = 'Item_Table';
// Frontend-only pseudo-type for FormulaItem sub-kinds, distinguished by their "Result Type" property.
export const ML_MODEL_TYPE = 'ML_Model';
export const TRENDABLE_TYPES = [
  'Annotation',
  SeeqNames.Types.CalculatedCondition,
  SeeqNames.Types.StoredCondition,
  SeeqNames.Types.CalculatedSignal,
  SeeqNames.Types.StoredSignal,
  SeeqNames.Types.CalculatedScalar,
  SeeqNames.Types.LiteralScalar,
  SeeqNames.Types.ThresholdMetric,
];
export const UNITFUL_TYPES = [
  SeeqNames.Types.CalculatedSignal,
  SeeqNames.Types.StoredSignal,
  SeeqNames.Types.CalculatedScalar,
  SeeqNames.Types.LiteralScalar,
];
export const ITEM_ICONS = {
  SERIES: 'fc fc-series',
  STRING_SERIES: 'fa fa-font',
  CAPSULE: 'fc fc-capsule',
  CONDITION: 'fc fc-capsule-set',
  ANNOTATION: 'fc fc-annotate',
  ASSET: 'fa fa-cube',
  SCALAR: 'fc fc-scalar',
  TABLE: 'fc fc-bar-chart',
  TABLE_DEFINITION: 'fc fc-scaling-table',
  ITEM_TABLE_DEFINITION: 'fc fc-tree-builder-hammer',
  METRIC: 'fc fc-metric',
  DATAFILE: 'fc fc-import',
  DISPLAY: 'fc fc-displays',
  HIERARCHY_NODE: 'fa fa-square',
  HIERARCHY: 'fc fc-asset-tree',
  HIERARCHY_ROOT: 'fc fc-top-tree',
  HIERARCHY_NODE_PARENT: 'fc fc-mid-tree',
  TOPIC: 'fc fc-report',
  ANALYSIS: 'fc fc-analysis',
  MODEL: 'fc fc-models',
};
export const NUMBER_CONVERSIONS = {
  NANOSECONDS_PER_MILLISECOND: 1000000,
  MILLISECONDS_PER_DAY: 1000 * 60 * 60 * 24,
  MILLISECONDS_PER_SECOND: 1000,
  SECONDS_PER_HOUR: 3600,
};
export const DISPLAY_MODE = {
  NEW: 'NEW',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  IN_PROGRESS: 'IN_PROGRESS',
  RESULTS: 'RESULTS',
};
export const EDIT_MODE = {
  NORMAL: 'NORMAL',
  COPY: 'COPY',
  COPY_TO_FORMULA: 'COPY_TO_FORMULA',
};
/**
 * @deprecated This constant should be imported from `@seeqdev/api`.
 */
export const APPSERVER_API_PREFIX = API_APPSERVER_API_PREFIX;
export const APPSERVER_PATHS = {
  WEBSOCKET_EVENTS: '/events',
  JUPYTER_EVENTS: '/data-lab',
};
/**
 * @deprecated This constant should be imported from `@seeqdev/api`.
 */
export const APPSERVER_API_CONTENT_TYPE = API_APPSERVER_API_CONTENT_TYPE;
export const ACCESS_LEVELS = {
  FULL_CONTROL: 'FULL_CONTROL',
  EDIT: 'EDIT',
  VIEW: 'VIEW',
  NO_ACCESS: 'NO_ACCESS',
};
export const MAX_DURATION_WARNING_MSG_PATH =
  'warningLogs[0].formulaLogEntries.EXCEEDS_MAXIMUM_DURATION.logDetails[0].message';
// max duration we recommend for advanced searches, in months. Maximum Capsule Durations that exceed this limit will
// display a warning prompt that encourages users to switch to a simple condition.
export const RECOMMENDED_MAX_CAPSULE_DURATION = 3;
export const GUID_REGEX_PATTERN = '[A-Fa-f0-9]{8}-(?:[A-Fa-f0-9]{4}-){3}[A-Fa-f0-9]{12}';
// Use with lodash, _.get(timeUnit, STANDARD_TIME_UNIT_PATH) to get the standard unit out.
export const STANDARD_TIME_UNIT_PATH = 'unit[0]';
// The standard unit label must be first in the 'unit' array so that the path above works. The standard unit
// should be the only unit that is dehydrated as part of the `UIConfig` and worksteps. Other units are aliases
// that we use at runtime to handle a variety of units from the backend.
export const DURATION_TIME_UNITS: DurationTimeUnit[] = [
  {
    unit: ['s', 'second', 'seconds'],
    momentUnit: 's',
    translationKey: 'DURATION_UNITS.SECONDS',
  },
  {
    unit: ['min', 'minute', 'minutes'],
    momentUnit: 'm',
    translationKey: 'DURATION_UNITS.MINUTES',
  },
  {
    unit: ['h', 'hr', 'hrs', 'hour', 'hours'],
    momentUnit: 'h',
    translationKey: 'DURATION_UNITS.HOURS',
  },
  {
    unit: ['day', 'd', 'days'],
    momentUnit: 'd',
    translationKey: 'DURATION_UNITS.DAYS',
  },
];
export const DURATION_TIME_UNITS_ALL = DURATION_TIME_UNITS.concat([
  {
    unit: ['week', 'wk', 'weeks'],
    momentUnit: 'w',
    translationKey: 'DURATION_UNITS.WEEKS',
  },
  {
    unit: ['month', 'mo', 'months'],
    momentUnit: 'M',
    translationKey: 'DURATION_UNITS.MONTHS',
  },
  {
    unit: ['year', 'y', 'yr', 'years'],
    momentUnit: 'y',
    translationKey: 'DURATION_UNITS.YEARS',
  },
]);

export const MOMENT_DURATION_UNITS = {
  SECONDS: 's',
  MINUTES: 'm',
  HOURS: 'h',
  DAYS: 'd',
  WEEKS: 'w',
  MONTHS: 'M',
  YEARS: 'y',
} as const;

export type MomentDurationUnit = (typeof MOMENT_DURATION_UNITS)[keyof typeof MOMENT_DURATION_UNITS];

// Units of measure for duration-based lengths that can be parsed by the backend (see ScalarUnits)
export { DURATION_SCALAR_UNITS } from '@/utilities/sharedDefinition.constants';
export const FREQUENCY_UNITS: DurationTimeUnit[] = [
  { unit: ['Hz', 'hertz'], translationKey: 'FREQUENCY_UNITS.HERTZ' },
  { unit: ['/min'], translationKey: 'FREQUENCY_UNITS.PER_MINUTE' },
  { unit: ['/h'], translationKey: 'FREQUENCY_UNITS.PER_HOUR' },
  { unit: ['/day'], translationKey: 'FREQUENCY_UNITS.PER_DAY' },
];
export const MIME_TYPES = {
  EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  POWERPOINT: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  PDF: 'application/pdf',
};
export const HTTP_MAX_BODY_SIZE_BYTES = 95000;
export const NG_IF_WAIT = 100;
export const LIMIT_SIZE_1000 = 1000;
export const MAX_NAME_LENGTH = {
  WORKBOOK: 250, // allows topics, analyses and folders to have longer and more descriptive names
  WORKSHEET: 100, // shorter names for worksheets to allow for better readability on side panel
  TOOL: 100, // shorter names for tools to allow for better UI experience where tool names are seen
  CALCULATION: 100, // shorter names for calculations allows for more columns to be seen at once in the details panel
  NAME: 35, // suggested length limit for a first or last name from stack overflow
};
export const PERIOD_UNITS = DURATION_TIME_UNITS.concat({
  unit: ['Hz', 'hertz'],
  translationKey: 'FREQUENCY_UNITS.HERTZ',
});

export const DEFAULT_PERMISSIONS = {
  read: false,
  write: false,
  manage: false,
};

export type ACLPermissionName = keyof typeof DEFAULT_PERMISSIONS;

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  MANAGE: 'manage',
} as const;

export const SEEQ_DATASOURCE = {
  id: 'Seeq',
  name: 'Seeq Databases',
  datasourceClass: 'Seeq Local',
  datasourceId: 'Collected',
  type: 'dataSource',
  scopedTo: '',
};

export const SMALL_SIZE_SCREEN_LIMIT = 815;
export const SMALL_SIZE_TABLE_VIEW_SCREEN_LIMIT = 1000;
export const EXTRA_SMALL_SIZE_TABLE_VIEW_SCREEN_LIMIT = 550;
export const SMALL_SIZE_SCREEN_LIMIT_SCATTER = 1050;
export const SMALL_SIZE_SCREEN_LIMIT_DENSITY = 800;
export const EXTRA_SMALL_SIZE_SCREEN_LIMIT_SCATTER = 670;
export const EXTRA_SMALL_SIZE_SCREEN_LIMIT_DENSITY = 540;

export enum HOME_SCREEN_TABS {
  USERS = 'USERS',
  CORPORATE = 'CORPORATE',
  HOME = 'HOME',
  MY_FOLDER = 'MY_FOLDER',
  RECENT = 'RECENT',
  SEARCH = 'SEARCH',
  SHARED = 'SHARED',
  TRASH = 'TRASH',
  ADDONS = 'ADDONS',
  AGENT_BUILDER = 'AGENT_BUILDER',
  TREE_BUILDER = 'TREE_BUILDER',
  EXTERNALLY_SHARED = 'EXTERNALLY_SHARED',
}

// This determines the order of the tabs
export const HOME_SCREEN_TABS_AND_TRANSLATION_KEYS = [
  {
    value: HOME_SCREEN_TABS.HOME,
    text: 'HOME_SCREEN.TABS.HOME',
    adminOnly: false,
    id: 'specHomeTab',
  },
  {
    value: HOME_SCREEN_TABS.MY_FOLDER,
    text: 'HOME_SCREEN.TABS.MY_FOLDER',
    folderRoot: 'all',
    adminOnly: false,
    id: 'myFolder',
  },
  {
    value: HOME_SCREEN_TABS.SHARED,
    text: 'HOME_SCREEN.TABS.SHARED',
    folderRoot: 'shared',
    adminOnly: false,
    id: 'specSharedTab',
  },
  {
    value: HOME_SCREEN_TABS.EXTERNALLY_SHARED,
    text: 'HOME_SCREEN.TABS.EXTERNALLY_SHARED',
    folderRoot: 'externallyShared',
    adminOnly: false,
    id: 'specExternallySharedTab',
  },
  {
    value: HOME_SCREEN_TABS.CORPORATE,
    text: 'HOME_SCREEN.TABS.CORPORATE',
    folderRoot: 'corporate',
    adminOnly: false,
    id: 'specCorporateTab',
  },
  {
    value: HOME_SCREEN_TABS.USERS,
    text: 'HOME_SCREEN.TABS.USERS',
    folderRoot: 'users',
    adminOnly: true,
    id: 'specUsersTab',
  },
  {
    value: HOME_SCREEN_TABS.TRASH,
    text: 'HOME_SCREEN.TABS.TRASH',
    adminOnly: false,
    id: 'specTrashTab',
  },
  {
    value: HOME_SCREEN_TABS.ADDONS,
    text: 'HOME_SCREEN.TABS.ADDONS',
    adminOnly: false,
    id: 'specAddOnsTab',
  },
  {
    value: HOME_SCREEN_TABS.AGENT_BUILDER,
    text: 'HOME_SCREEN.TABS.AGENT_BUILDER',
    adminOnly: false,
    id: 'specAgentBuilderTab',
  },
  {
    value: HOME_SCREEN_TABS.TREE_BUILDER,
    text: 'TREE_BUILDER.TITLE',
    adminOnly: false,
    id: 'specTreeBuilderTab',
  },
];

export const USERS_FOLDER_NAME = 'Users folder';

export const SEARCH_ITEM_LOCATIONS = {
  ALL: 'all',
  USERS: 'users',
  CORPORATE: 'corporate',
  MY_FOLDER: 'mine',
  PINNED: 'pinned',
  RECENT: 'recent', // We don't use this for homescreen advanced search  CRAB-19839
  TRASH: 'trash',
  SHARED_OR_PUBLIC: 'sharedOrPublic',
  EXTERNALLY_SHARED: 'externallyShared',
} as const;
export type SearchItemLocation = (typeof SEARCH_ITEM_LOCATIONS)[keyof typeof SEARCH_ITEM_LOCATIONS];

export const SEARCH_FIELDS = {
  ALL: 'all',
  NAME: 'name',
  CONTENT: 'document',
  DESCRIPTION: 'description',
};

export const ADVANCED_SEARCH = {
  SEARCH_TEXT: 'searchText',
  TYPE: 'type',
  LOCATION: 'location',
  SEARCH_FIELDS: 'searchFields',
};

// This determines the order of the search location options
export const SEARCH_LOCATION_OPTIONS = [
  {
    text: 'HOME_SCREEN.LOCATION.RECENT',
    value: SEARCH_ITEM_LOCATIONS.RECENT,
  },
  {
    text: 'HOME_SCREEN.LOCATION.PINNED',
    value: SEARCH_ITEM_LOCATIONS.PINNED,
  },
  {
    text: 'HOME_SCREEN.LOCATION.MY_FOLDER',
    value: SEARCH_ITEM_LOCATIONS.MY_FOLDER,
  },
  {
    text: 'HOME_SCREEN.LOCATION.SHARED',
    value: SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC,
  },
  {
    text: 'HOME_SCREEN.LOCATION.EXTERNALLY_SHARED',
    value: SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED,
  },
  {
    text: 'HOME_SCREEN.LOCATION.CORPORATE',
    value: SEARCH_ITEM_LOCATIONS.CORPORATE,
  },
  {
    text: 'HOME_SCREEN.LOCATION.USERS',
    value: SEARCH_ITEM_LOCATIONS.USERS,
    adminOnly: true,
  },
  {
    text: 'HOME_SCREEN.LOCATION.ALL',
    value: SEARCH_ITEM_LOCATIONS.ALL,
  },
];

export const SEARCH_LOCATION_OPTIONS_ADVANCED = [
  ...SEARCH_LOCATION_OPTIONS,
  {
    text: 'HOME_SCREEN.LOCATION.TRASH',
    value: SEARCH_ITEM_LOCATIONS.TRASH,
  },
];

export const KEY_CODES = {
  ENTER: 13,
  ESCAPE: 27,
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
  TAB: 9,
};

export const CORPORATE_DRIVE_NAME = 'Corporate';
export const CREATED_BY_SEEQ_WORKBENCH = 'Seeq Workbench';
export const SOCKET_LIVELINESS_TIMEOUT = 5 * 60 * 1000;
export const TEST_ENV = 'test';

// keep in sync with $sq-animation-time-ms variable from _custom_variables.scss
export const ANIMATION_TIME_MS = 400;

export const DELIMITERS: { text: string; value: 'Comma' | 'Semicolon' | 'Tab' }[] = [
  { text: ',', value: 'Comma' },
  { text: ';', value: 'Semicolon' },
  { text: 'Tab', value: 'Tab' },
];

export type Delimiter = (typeof DELIMITERS)[number]['value'];

/**
 * An extra 1 is added to indicate when the number of rows exceed the limit of `500,000`
 */
export const MAX_TABLE_ROWS = 500001;

export const INPUT_MIN_SIZE = 100;
export const AUTO_WIDTH_OFFSET = 40;
